<template>
  <v-dialog v-model="isOpenDetailDialog" fullscreen>
    <div v-if="isDisabled" :class="$style.dialogDisableArea"></div>
    <v-card>
      <div v-if="isLoading" :class="$style.wrapper">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <template v-else>
        <v-toolbar
          fixed
          flat
          dark
          height="56px"
          style="
            background-color: rgba(0, 0, 0, 0.7); border-top-right-radius: 0; border-top-left-radius: 0;"
        >
          <div :class="$style.toolbar">
            <v-spacer></v-spacer>

            <div :class="['subheading', $style.toolbar__title]">
              {{ contents.title }}
            </div>

            <v-spacer></v-spacer>

            <v-btn
              small
              flat
              fab
              :class="$style.toolbar__closeIcon"
              style="position: absolute;"
              @click="
                isOpenDetailDialog = false;
                isLoading = true;
                revertUrl();
                isDisabled = false;
              "
            >
              <closeIcon></closeIcon>
            </v-btn>
          </div>
        </v-toolbar>

        <v-content class="pt-5">
          <v-container
            grid-list-lg
            fluid
            :class="{ 'px-5': $vuetify.breakpoint.mdAndUp }"
          >
            <v-layout justify-center row wrap>
              <vc-share-deleted-contents-alert
                v-if="contents.isDeleted"
                :is-user-delete="contents.deleteType"
                :lang="lang.DeletedContentsAlert"
                :location-href="applicationUriBase + '/' + locale"
              ></vc-share-deleted-contents-alert>

              <v-flex v-else xs12 sm10 md10 lg9 xl8>
                <v-card class="mb-4">
                  <v-card-text>
                    <v-layout row wrap>
                      <v-flex
                        xs12
                        :class="[
                          'pt-0',
                          {
                            'pr-0': $vuetify.breakpoint.smOnly,
                            'pl-4': $vuetify.breakpoint.mdAndUp
                          }
                        ]"
                      >
                        <v-flex xs12 class="px-0">
                          <div>
                            <div class="body-2 pb-3">
                              <span data-contents-description>
                                <vc-share-auto-linked-text>
                                  <vc-share-nl2br
                                    tag="span"
                                    :text="contents.description"
                                  ></vc-share-nl2br>
                                </vc-share-auto-linked-text>
                              </span>

                              <connect-vc-share-ogp-list
                                class="mt-3"
                                :content="contents.description"
                              ></connect-vc-share-ogp-list>
                            </div>

                            <div
                              v-if="contents.locale !== locale"
                              class="text-xs-right"
                            >
                              <connect-vc-share-contents-translation-button
                                :code="contents.identificationCode"
                                :lang="lang.ContentsTranslationButton"
                              >
                              </connect-vc-share-contents-translation-button>
                            </div>
                          </div>
                        </v-flex>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
                <v-card class="mb-4">
                  <v-card-text>
                    <v-layout row wrap>
                      <v-flex xs12 class="pt-0">
                        <v-flex xs12 class="d-flex px-0">
                          <v-flex
                            xs6
                            :class="[
                              'text-xs-right text-sm-right text-md-center',
                              {
                                'px-0': $vuetify.breakpoint.mdAndUp,
                                'pl-0': $vuetify.breakpoint.smOnly
                              }
                            ]"
                          >
                            <connect-vc-share-comic-favorite-button
                              :is-logged-in="isLoggedIn"
                              :favorite-count="contents.favoriteCount"
                              :identification-code="contents.identificationCode"
                              :identification-code-hash="
                                contents.identificationCodeHash
                              "
                              :lang="lang.FavoriteButton"
                              :is-own-contents="contents.isOwnContents"
                              :locale="locale"
                            ></connect-vc-share-comic-favorite-button>
                          </v-flex>
                          <v-flex
                            xs6
                            :class="[
                              'text-xs-left text-sm-left text-md-center',
                              { 'px-0': $vuetify.breakpoint.smOnly }
                            ]"
                          >
                            <connect-vc-share-star-button
                              :locale="locale"
                              :initial-starred-count="contents.starredCount"
                              :initial-star-state="contents.isStarred"
                              :contents-id="contents.id"
                              :identification-code="contents.identificationCode"
                              :identification-code-hash="
                                contents.identificationCodeHash
                              "
                              :lang="lang.StarButton"
                              :is-logged-in="isLoggedIn"
                              :contents-url="
                                localedUrl +
                                  'contents/' +
                                  contents.identificationCode
                              "
                              :href="
                                localedUrl +
                                  'starred?addBook=' +
                                  contents.identificationCode
                              "
                              :is-wall="true"
                              :is-my-wall="isMyWall"
                              :is-starred-page="isStarredPage"
                              :wall-identification-code="wallIdentificationCode"
                              :login-url="createLoginUrl()"
                            >
                            </connect-vc-share-star-button>
                          </v-flex>
                        </v-flex>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>

                <template v-if="contents.isPostCommentEnabled">
                  <div class="headline mb-3" data-post-comment>
                    {{ lang.Detail.comment }}
                  </div>

                  <v-card class="mb-4">
                    <v-card-text>
                      <connect-vc-share-post-comment
                        :selected-comment-account="selectedCommentAccount"
                        :clip-account-info="clipAccountInfo"
                        :twitter-account-info="twitterAccountInfo"
                        :locale="locale"
                        :lang="lang.PostComment"
                        :validation-lang="lang.validation"
                        :locale-code="localeCode"
                        :identification-code="contents.identificationCode"
                        :is-double-iframe="isDoubleIframe"
                        :page-name="pageName"
                        :position-index="Number(positionIndex)"
                      >
                      </connect-vc-share-post-comment>
                    </v-card-text>
                  </v-card>

                  <v-card class="mb-4">
                    <connect-vc-share-comment-list
                      :contents-id="contents.id"
                      :clip-user-id="
                        clipAccountInfo.isLoggedIn ? clipAccountInfo.userId : 0
                      "
                      :twitter-user-id="
                        twitterAccountInfo.isLoggedIn
                          ? twitterAccountInfo.userId
                          : 0
                      "
                      :locale="locale"
                      :lang="{
                        CommentList: this.lang.CommentList,
                        CommentMenuButton: this.lang.CommentMenuButton,
                        ContentsTranslationButton: this.lang
                          .ContentsTranslationButton,
                        ShareIcons: this.lang.ShareIcons
                      }"
                      :clip-is-logged-in="isLoggedIn"
                      :clip-login-url="loginUrl"
                      :validation-lang="lang.validation"
                      :locale-code="localeCode"
                      :identification-code="contents.identificationCode"
                    ></connect-vc-share-comment-list>
                  </v-card>
                </template>

                <v-card class="mb-4">
                  <v-card-text>
                    <v-layout row wrap>
                      <v-flex xs12 class="pb-3">
                        <h1
                          :class="[
                            'font-weight-bold',
                            {
                              subheading: $vuetify.breakpoint.xsOnly,
                              title: $vuetify.breakpoint.smOnly,
                              headline: $vuetify.breakpoint.mdAndUp
                            }
                          ]"
                        >
                          {{ contents.title }}
                        </h1>
                      </v-flex>

                      <v-flex
                        xs9
                        sm6
                        md5
                        :class="[
                          'mx-auto',
                          { 'd-flex': $vuetify.breakpoint.xsOnly }
                        ]"
                      >
                        <vc-share-thumbnail-image
                          :img-src="
                            contents.thumbnailImageUrl +
                              '?' +
                              contents.updatedAtTag
                          "
                          :hover-event="false"
                          :display-icon="true"
                        ></vc-share-thumbnail-image>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>

                <v-card class="mb-4">
                  <v-card-text>
                    <v-layout row wrap>
                      <v-flex
                        xs12
                        :class="[
                          'pt-0',
                          {
                            'pr-0': $vuetify.breakpoint.smOnly,
                            'pl-4': $vuetify.breakpoint.mdAndUp
                          }
                        ]"
                      >
                        <div class="pt-2 pb-2">
                          <vc-share-avatar-image
                            class="mr-2"
                            :size="50"
                            :src="contents.creatorProfileImageUrl"
                          >
                          </vc-share-avatar-image>
                          <span>{{ contents.creatorProfileHandle }}</span>
                        </div>
                        <v-layout row wrap reverse class="pb-3">
                          <v-flex>
                            <div class="body-1">
                              {{ lang.Detail.publish_date }} :
                              {{ contents.publishedAt }}
                            </div>

                            <v-flex xs12 class="d-flex px-0 py-2">
                              <v-flex
                                xs7
                                :class="[
                                  'px-0',
                                  { 'd-flex': $vuetify.breakpoint.xsOnly }
                                ]"
                              >
                                <v-flex class="d-inline-block pl-0 pr-3">
                                  <vc-share-post-source-icon></vc-share-post-source-icon>
                                  {{ lang.Site["locale_" + contents.locale] }}
                                </v-flex>
                                <v-flex class="d-inline-block pr-3">
                                  <v-icon small>far fa-eye</v-icon>
                                  {{ contents.previewCount }}
                                </v-flex>

                                <v-flex class="d-inline-block pr-3">
                                  <v-icon small>fas fa-comment</v-icon>
                                  {{ contents.commentCount }}
                                </v-flex>
                              </v-flex>
                              <v-flex xs5 class="text-xs-right px-0">
                                <connect-vc-share-menu-button
                                  :contents-id="contents.id"
                                  :show-contents-report="true"
                                  :clip-is-logged-in="isLoggedIn"
                                  :clip-login-url="loginUrl"
                                  :locale-code="localeCode"
                                  :validation-lang="lang.validation"
                                  :lang="lang.MenuButton"
                                ></connect-vc-share-menu-button>
                              </v-flex>
                            </v-flex>
                          </v-flex>
                        </v-layout>
                        <div class="text-xs-right pt-1">
                          <a
                            v-if="contents.canDownLoadPrintingData"
                            :href="
                              localedUrl +
                                'contents/download?code=' +
                                contents.identificationCode
                            "
                          >
                            {{
                              lang.DownloadPrintingData.download_printing_data
                            }}
                          </a>
                        </div>
                      </v-flex>
                      <v-flex
                        xs12
                        :class="[
                          'text-xs-right pt-0',
                          { 'px-0': $vuetify.breakpoint.smOnly }
                        ]"
                      >
                        <vc-share-icons
                          :twitter-text="contents.twitterText"
                          :twitter-hash-tags="contents.twitterHashTags"
                          :line-text="contents.lineText"
                          :src-url="contents.snsShareUrl"
                          :lang="lang.ShareIcons"
                          :show-japanese-web-site="locale === 'ja-jp'"
                        ></vc-share-icons>
                      </v-flex>
                      <v-flex
                        xs12
                        sm5
                        offset-sm7
                        :class="[
                          'text-xs-right',
                          { 'px-0': $vuetify.breakpoint.smOnly }
                        ]"
                      >
                        <vc-share-iframe-embed-html
                          :lang-text="lang.Site.OEmbed_html"
                          :embed-html="contents.embedHtml"
                        >
                        </vc-share-iframe-embed-html>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>

                <v-card class="mb-4">
                  <v-card-text :class="['py-0']">
                    <v-layout row wrap>
                      <v-flex xs12 sm12 md12 lg12>
                        <div class="pt-1 pb-2">
                          <vc-share-avatar-image
                            class="mr-2"
                            :size="25"
                            :src="contents.creatorProfileImageUrl"
                          >
                          </vc-share-avatar-image>
                          {{ userProfileText }}
                          <vc-share-a
                            :href="contents.creatorProfileUrl"
                            target="_blank"
                            style="text-decoration: none;"
                          >
                            {{ lang.Detail.more_detail }}
                          </vc-share-a>
                        </div>
                        <div>{{ contents.creatorProfileDescription }}</div>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-content>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import qs from "qs";
import closeIcon from "./assets/close_icon.svg";

export default {
  name: "vc-share-contents-detail-dialog",
  components: {
    closeIcon: closeIcon
  },
  props: {
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    loginUrl: {
      type: String,
      default: ""
    },
    applicationUriBase: {
      type: String,
      default: ""
    },
    localedUrl: {
      type: String,
      default: ""
    },
    locale: {
      type: String,
      default: "ja-jp"
    },
    localeCode: {
      type: String,
      default: ""
    },
    lang: {
      type: Object,
      default: () => {
        return {
          DeletedContentsAlert: {},
          Detail: {},
          PostComment: {},
          MenuButton: {},
          DownloadPrintingData: {},
          ShareIcons: {},
          CommentList: {},
          CommentMenuButton: {},
          ContentsTranslationButton: {},
          FavoriteButton: {},
          StarButton: {},
          Site: {},
          validation: {}
        };
      }
    },
    initialIdentificationCode: {
      type: String,
      default: ""
    },
    initialPositionIndex: {
      type: String,
      default: ""
    },
    selectedCommentAccount: {
      type: String,
      default: ""
    },
    clipAccountInfo: {
      type: Object,
      default: () => {
        return {
          loginUrl: "",
          userId: 0,
          userName: "",
          userImagePath: "",
          isLoggedIn: false
        };
      }
    },
    twitterAccountInfo: {
      type: Object,
      default: () => {
        return {
          loginUrl: "",
          logoutUrl: "",
          userId: 0,
          userName: "",
          userImagePath: "",
          isLoggedIn: false
        };
      }
    },
    wallIdentificationCode: {
      type: String,
      default: ""
    },
    contentsPageIdentificationCode: {
      type: String,
      default: ""
    },
    isDoubleIframe: {
      type: Boolean,
      default: false
    },
    pageName: {
      type: String,
      default: ""
    },
    isMyWall: {
      type: Boolean,
      default: false
    },
    isStarredPage: {
      type: Boolean,
      default: false
    },

    // actionsToProps
    getContentsDetailInfo: {
      type: Function,
      default: () => {}
    }
  },
  data: () => {
    return {
      userProfileText: "",
      isOpenDetailDialog: false,
      isLoading: true,
      contents: {},
      contentsIdentificationCode: "",
      positionIndex: 0,
      isDisabled: false
    };
  },
  mounted() {
    const { showInfo } = qs.parse(window.location.search.replace("?", ""));
    if (showInfo === "1") {
      this.contentsIdentificationCode = this.initialIdentificationCode;
      this.positionIndex = this.initialPositionIndex;
      this.callGetContentsDetailData(this.initialIdentificationCode);
    }

    window.addEventListener("message", event => {
      if (event.data.event === "currentContentsInfo") {
        this.isDisabled = true;
        this.callGetContentsDetailData(event.data.identification_code);
        this.contentsIdentificationCode = event.data.identification_code;
        this.positionIndex = event.data.position_index;

        const replaceUrlParams = {
          identification_code: event.data.identification_code,
          position_index: event.data.position_index
        };
        this.replaceUrl(replaceUrlParams);
      }
    });
  },
  methods: {
    createLoginUrl() {
      const { show2dViewer } = qs.parse(
        window.location.search.replace("?", "")
      );
      let url = "/auth/clip/login";
      if (this.isDoubleIframe) {
        url =
          url +
          "?isDoubleIframe=1&pageName=" +
          this.pageName +
          "&contentsIdentificationCode=" +
          this.contents.identificationCode;
      }
      if (show2dViewer === "1") {
        url = url + "&show2dViewer=1";
      }
      return url;
    },
    callGetContentsDetailData(identificationCode) {
      this.isOpenDetailDialog = true;
      this.getContentsDetailInfo(identificationCode).then(res => {
        const resStatus = res.status;
        const contentsData = res.contentsDetailInfo;
        if (resStatus !== 0) {
          return;
        }

        this.contents = contentsData;
        this.$nextTick(() => {
          this.userProfileText = this.lang.Detail.user_profile.replace(
            ":clip_user_handle",
            contentsData.creatorProfileHandle
          );
          this.isLoading = false;
        });
      });
    },
    replaceUrl(params) {
      // コールバックURLで再度同じ状態で表示するためにURLを変更
      let urlParameters = {};
      if (this.wallIdentificationCode !== "") {
        urlParameters.code = this.wallIdentificationCode;
      }
      if (this.contentsPageIdentificationCode !== "") {
        urlParameters.code = this.contentsPageIdentificationCode;
      }

      urlParameters.contentsIdentificationCode = params.identification_code;
      urlParameters.positionIndex = params.position_index;
      urlParameters.showInfo = 1;

      const callbackUrl =
        document.location.origin +
        document.location.pathname +
        "?" +
        qs.stringify(urlParameters);
      window.history.replaceState(null, null, callbackUrl);
    },
    revertUrl() {
      let url = document.location.origin + document.location.pathname;

      let urlParameters = {};
      if (this.wallIdentificationCode !== "") {
        urlParameters = { code: this.wallIdentificationCode };
      }
      if (this.contentsPageIdentificationCode !== "") {
        urlParameters.code = this.contentsPageIdentificationCode;
      }

      urlParameters.contentsIdentificationCode = this.contentsIdentificationCode;
      urlParameters.positionIndex = this.positionIndex;

      url += "?" + qs.stringify(urlParameters);
      window.history.replaceState(null, null, url);
    }
  }
};
</script>

<style module lang="scss">
.dialogDisableArea {
  position: fixed;
  width: 100%;
  height: 100%;
}

.wrapper {
  background-color: rgba(255, 255, 255, 0.5);
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.toolbar {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;

  &__title {
    max-width: 980px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 32px 0 32px;
  }

  &__closeIcon {
    fill: white;
    margin: 0;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }
}

.embedViewerDialog {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
}
</style>
